import { hostBridge } from '@publicala/delfino';
import type { ListenSynthesizePayload } from '@publicala/delfino';
import { createDefaultListenMock, isE2EMockActive } from '../../e2eMockGuard';

/**
 * Listen API for Volpe.
 * Provides methods to synthesize speech via hostBridge.
 */
class ListenAPI {
    /**
     * Synthesize speech from text.
     * Returns mock data in E2E standalone mode.
     *
     * @param payload - Synthesize parameters matching Delfino's ListenSynthesizePayload
     * @param payload.text - The text to synthesize
     * @param payload.voiceId - Optional voice identifier
     * @param payload.lang - Optional language code
     * @returns Audio blob ready to use
     */
    async synthesizeSpeech(payload: ListenSynthesizePayload): Promise<Blob> {
        if (isE2EMockActive()) {
            return createDefaultListenMock();
        }
        const response = await hostBridge.listen.synthesize(payload);
        return this.base64ToBlob(response.audio, response.contentType);
    }

    /**
     * Converts base64 encoded audio to a Blob.
     * @param base64 - Base64 encoded audio string
     * @param contentType - MIME type for the audio
     */
    private base64ToBlob(base64: string, contentType = 'audio/mpeg'): Blob {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: contentType });
    }
}

export const listenAPI = new ListenAPI();
